import { graphql } from 'gatsby';
import React from 'react';

import { Prismic_Page } from 'types/prismic';
import { IPageContext, IPageSibling } from 'types/pageContext';
import { PrismicImage } from 'types/prismicImage';

import { metaFragment } from 'graphql/metaFragment';
import { personFragment } from 'graphql/personFragment';
import { externalCtaFragment } from 'graphql/externalCtaFragment';
import { articleFragment } from 'graphql/articleFragment';
import { pageFragment } from 'graphql/pageFragment';
import { linkableFragment } from 'graphql/linkableFragment';
import { projectFragment } from 'graphql/projectFragment';
import { Seo } from 'components/seo/Seo';
import { associationFragment } from '../graphql/associationFragment';
import { pageSlices } from 'graphql/slices';

import { linkResolver } from 'utils/linkResolver';

import { asText } from 'components/rich-text/RichText';
import { Link } from 'components/link/Link';
import { Slices } from 'containers/slices/SliceContainer';
import { Header } from 'components/header/Header';
import { PageNav, Item } from 'components/page-nav/PageNav';
import { StickyWrapper } from 'components/sticky-wrapper/StickyWrapper';

interface IPageProps {
  data?: {
    prismic?: {
      page?: Prismic_Page | null;
    };
  };
  pageContext?: IPageContext;
}

export default function Page(props: IPageProps) {
  const page: Prismic_Page | null = props?.data?.prismic?.page ?? null;
  const context = props?.pageContext ?? null;

  if (!page) {
    return null;
  }

  const renderSidebar = () => {
    return (
      <PageNav heading={asText(page.title)} parentHeading={context?.parent.title || undefined}>
        {(context?.siblings ?? []).map((sibling: IPageSibling, i: number) => {
          const title = asText(sibling?.title) || asText(sibling?.link?.title) || null;
          const link = sibling?.link ?? null;

          if (!title || !link) {
            return null;
          }

          const active = context?.uid === sibling?.link?._meta?.uid;
          const Wrapper = active ? 'strong' : 'span';

          return (
            <Item key={i} active={active}>
              <Link to={linkResolver(link)}>
                <Wrapper>{title}</Wrapper>
              </Link>
            </Item>
          );
        })}
      </PageNav>
    );
  };

  return (
    <>
      <Seo
        title={asText(page?.seo_title) || asText(page.title)}
        description={asText(page?.seo_description)}
        image={(page?.seo_image ?? null) as PrismicImage}
      />

      <Header logo={false} />

      <StickyWrapper lead={renderSidebar()}>
        <Slices data={page.body ?? []} isColumnLayout></Slices>
      </StickyWrapper>
    </>
  );
}

export const query = graphql`
  query Page($uid: String!, $lang: String = "is") {
    prismic {
      page(uid: $uid, lang: $lang) {
        _meta {
          ...meta
        }
        title
        body {
          ...pageSlices
        }
        seo_title
        seo_description
        seo_image
      }
    }
  }
`;

Page.query = query;
Page.fragments = [
  metaFragment,
  linkableFragment,
  pageFragment,
  articleFragment,
  personFragment,
  pageSlices,
  projectFragment,
  associationFragment,
  externalCtaFragment,
];
